"use client";

import { Modal, ModalContent, ModalHeader, ModalBody } from "@heroui/modal";
import { Input } from "@heroui/input";
import { useEffect, useState } from "react";
import { useModal } from "@/contexts/modal-context";
import { useActivateReferral } from "@/hooks/challenges";
import { Button } from "./ui/button";
import { Gift } from "lucide-react";

export const ReferralModal = () => {
  const { isReferralModalVisible, hideReferralModal } = useModal();
  const [value, setValue] = useState<string>("");
  const activateReferralMutation = useActivateReferral();

  useEffect(() => {
    if (isReferralModalVisible && typeof window !== "undefined") {
      const storedReferral = localStorage.getItem("referralCode");
      setValue(storedReferral || "");
    }
  }, [isReferralModalVisible]);

  const handleClose = () => {
    hideReferralModal();
    setValue("");
    if (typeof window !== "undefined") {
      localStorage.removeItem("referralCode");
    }
  };

  const handleActivate = async () => {
    if (value && value.trim()) {
      try {
        await activateReferralMutation.mutateAsync(value.trim());
        handleClose();
      } catch (error) {
        // Error is handled in the mutation hook with toast
      }
    }
  };

  return (
    <Modal
      isOpen={isReferralModalVisible}
      onClose={handleClose}
      size="md"
      classNames={{
        base: "bg-white",
        backdrop: "bg-black/50",
      }}
    >
      <ModalContent className="p-6">
        <ModalHeader className="flex flex-col gap-1 pb-4">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-swapper-primary to-[#9F7FFF] flex items-center justify-center">
              <Gift className="w-8 h-8 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-swapper-secondary text-center">
            Activate Referral
          </h2>
          <p className="text-sm text-swapper-secondary/60 text-center font-normal mt-2">
            Enter your referral code to unlock rewards and start earning points!
          </p>
        </ModalHeader>

        <ModalBody className="pt-4 pb-6">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            label="Referral Code"
            placeholder="Enter referral code"
            variant="bordered"
            size="lg"
            classNames={{
              input: "text-base",
              label: "text-swapper-secondary/70 font-medium",
              inputWrapper: [
                "border-2",
                "border-gray-200",
                "hover:border-swapper-primary/50",
                "focus-within:!border-swapper-primary",
                "bg-white",
                "rounded-xl",
                "transition-all",
                "duration-200",
              ],
            }}
          />

          <div className="flex gap-3 w-full mt-6">
            <Button
              variant="monochrome"
              onClick={handleClose}
              className="w-full"
              disabled={activateReferralMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-full"
              onClick={handleActivate}
              disabled={!value || !value.trim() || activateReferralMutation.isPending}
            >
              {activateReferralMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Activating...
                </span>
              ) : (
                "Activate"
              )}
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
